import { useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Star } from "lucide-react";
import { AdminListShell } from "@/components/admin/AdminListShell";
import { deleteBlogPost } from "@/lib/blog.functions";
import { adminBlogPostsQuery, INVALIDATE } from "@/lib/query-registry";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { blogCategoryLabel } from "@/lib/blog-categories";
import { formatUpdated } from "@/lib/article-utils";
import { ownershipPending } from "@/lib/blog-ownership-validation";
import { BLOG_LIFECYCLE_LABEL, blogLifecycle } from "@/lib/blog-schedule";

type BlogRow = {
  id: string;
  slug: string;
  slug_es: string | null;
  title: string;
  title_es: string | null;
  content_es: string | null;
  category: string;
  status: string;
  featured: boolean;
  published_at: string | null;
  updated_at: string | null;
  reading_minutes: number;
  noindex: boolean;
  tags: string[];
  related_resources: string[];
  related_services: string[];
};

export const Route = createFileRoute("/admin/blog/")({
  component: BlogList,
});

const LIST_KEY = INVALIDATE.adminBlog;

const STATUS_STYLES: Record<string, string> = {
  published: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  scheduled: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  draft: "bg-muted text-muted-foreground",
  archived: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
};

function BlogList() {
  const router = useRouter();
  const [locale, setLocale] = useState<"all" | "en" | "es">("all");
  const queryClient = useQueryClient();
  const query = useQuery(adminBlogPostsQuery());
  const allRows = (query.data?.posts ?? []) as BlogRow[];
  const rows = allRows.filter((post) => locale !== "es" || Boolean(post.slug_es && post.title_es && post.content_es));

  const deleteMutation = useAuthedMutation(deleteBlogPost, {
    invalidate: [INVALIDATE.adminBlog, INVALIDATE.publicBlog],
    invalidateOn: "settled",
    onMutate: async ({ id }: { id: string }) => {
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const previous = queryClient.getQueryData<{ posts: BlogRow[] }>(LIST_KEY);
      if (previous) {
        queryClient.setQueryData(LIST_KEY, {
          ...previous,
          posts: previous.posts.filter((p) => p.id !== id),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { previous?: { posts: BlogRow[] } } | undefined;
      if (ctx?.previous) queryClient.setQueryData(LIST_KEY, ctx.previous);
    },
    onSuccess: () => router.invalidate(),
  });

  const onDelete = (id: string) => {
    if (!confirm("Delete this post? This cannot be undone.")) return;
    deleteMutation.mutate({ id });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
            Blog
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Timely insights, engineering notes and analysis. Newest first.
          </p>
        </div>
        <Link
          to="/admin/blog/new"
          className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> New post
        </Link>
      </div>

      {deleteMutation.error && (
        <p className="mt-6 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {deleteMutation.error.message}
        </p>
      )}

      {rows.some((row) => row.status === "draft" && ownershipPending(row)) && (
        <p className="mt-6 text-sm text-muted-foreground">
          {rows.filter((row) => row.status === "draft" && ownershipPending(row)).length} drafts need
          SEO ownership review before publishing. Open Edit to select the supported resource or
          service.
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-2" role="group" aria-label="Filter blog by language">
        {(["all", "en", "es"] as const).map((value) => (
          <button key={value} type="button" aria-pressed={locale === value} onClick={() => setLocale(value)}
            className={`rounded-full border px-4 py-2 text-sm ${locale === value ? "bg-foreground text-background border-foreground" : "border-border text-foreground hover:bg-card"}`}>
            {value === "all" ? "Todos" : value === "en" ? "English" : "Español"}
          </button>
        ))}
      </div>
      <div className="mt-4 overflow-hidden rounded-2xl border border-border">
        <div className="grid grid-cols-[1fr_140px_120px_120px_150px] items-center gap-4 bg-card/50 px-4 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">
          <div>Title</div>
          <div className="hidden md:block">Category</div>
          <div>Status</div>
          <div className="hidden md:block">Published</div>
          <div className="text-right">Actions</div>
        </div>

        <AdminListShell
          query={query}
          isEmpty={(d) => (d.posts ?? []).length === 0}
          emptyText="No posts yet. Create your first one."
        >
          {() => (
            <>
              {rows.map((row) => {
                const spanish = locale === "es" && Boolean(row.slug_es && row.title_es);
                const displayTitle = spanish ? row.title_es : row.title;
                const displaySlug = spanish ? row.slug_es : row.slug;
                const lifecycle = blogLifecycle(row);
                return (
                  <div
                    key={row.id}
                    className="grid grid-cols-[1fr_140px_120px_120px_150px] items-center gap-4 border-t border-border bg-background px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">{displayTitle}</span>
                        {locale === "all" && row.title_es && row.slug_es && row.content_es && <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px]">EN + ES</span>}
                        {row.featured && (
                          <Star
                            className="h-3.5 w-3.5 shrink-0 fill-current text-amber-500"
                            aria-label="Featured"
                          />
                        )}
                        {row.noindex && (
                          <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                            noindex
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {spanish ? "/es/blog/" : "/blog/"}{displaySlug} · {row.reading_minutes} min
                      </div>
                    </div>
                    <div className="hidden truncate text-sm text-muted-foreground md:block">
                      {blogCategoryLabel(row.category)}
                    </div>
                    <div>
                      <span
                        className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-wider ${
                          STATUS_STYLES[lifecycle] ?? "bg-muted text-muted-foreground"
                        }`}
                      >
                        {BLOG_LIFECYCLE_LABEL[lifecycle]}
                      </span>
                      {row.status === "draft" && ownershipPending(row) && (
                        <span className="mt-2 block text-xs font-medium text-foreground">
                          SEO ownership pending
                        </span>
                      )}
                    </div>
                    <div className="hidden text-xs text-muted-foreground md:block">
                      {row.published_at ? formatUpdated(row.published_at, "en") : "—"}
                      {lifecycle === "scheduled" && (
                        <span className="block text-[10px] uppercase tracking-wider text-sky-600 dark:text-sky-400">
                          goes live automatically
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to="/admin/blog/$id"
                        params={{ id: row.id }}
                        search={{ lang: spanish ? "es" : "en" }}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-foreground hover:bg-card"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </Link>
                      <button
                        onClick={() => onDelete(row.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </AdminListShell>
      </div>
    </div>
  );
}
